import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ComparisonColumn, ComparisonView, HoldRowView } from '@nest-recruit/shared';
import { errorMessage } from '../../../lib/api-client';
import { useModule } from '../../../lib/modules';
import { useRequiredSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { comparisonApi, comparisonKey } from '../api/comparison-api';
import * as patch from '../comparison-patch';

export type ComparisonAction = 'select' | 'hold' | 'eliminate' | 'remove' | 'restore' | 'add' | 'release' | 'extend' | 'undo';

export interface PoolRelease {
  readonly reasonCode: string;
  readonly poolIds: readonly string[];
  readonly newPoolNames: readonly string[];
}

export interface ComparisonActions {
  /** Which application has an action on its way — feedback stays on that control (§5a rule 4). */
  readonly pendingOf: (applicationId: string) => ComparisonAction | null;
  readonly select: (column: ComparisonColumn) => void;
  /** The column's own Undo, for the ten minutes after the toast has gone. */
  readonly undoSelection: (column: ComparisonColumn) => void;
  readonly hold: (column: ComparisonColumn) => void;
  readonly eliminate: (column: ComparisonColumn, reasonCode: string, reasonLabel: string) => void;
  readonly remove: (column: ComparisonColumn) => void;
  readonly add: (applicationId: string, candidateName: string) => void;
  readonly restore: (applicationId: string, candidateName: string) => void;
  readonly releaseToOffer: (row: HoldRowView) => void;
  readonly releaseToPool: (row: HoldRowView, choice: PoolRelease) => void;
  readonly extend: (row: HoldRowView) => void;
}

interface Outcome {
  readonly message: string;
  readonly detail?: string;
  /** Undo beats confirm (§5a rule 8): a reversible change just happens, with ten seconds to take it back. */
  readonly undo?: () => Promise<ComparisonView>;
  readonly undoneMessage?: string;
}

/**
 * Every DEC-02 change, act first and confirm after (§5a rule 1): the screen changes at
 * once, the server's answer (the whole comparison) replaces it, and a refusal rolls it
 * back with the reason. The provisional state is held until that answer lands (rule 3).
 */
export function useComparisonActions(jobId: string): ComparisonActions {
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const session = useRequiredSession();
  const isOffersOn = useModule('offers');
  const [pending, setPending] = useState<ReadonlyMap<string, ComparisonAction>>(new Map());
  const key = comparisonKey(jobId);

  function mark(applicationId: string, action: ComparisonAction | null): void {
    setPending((current) => {
      const next = new Map(current);
      if (action === null) next.delete(applicationId);
      else next.set(applicationId, action);
      return next;
    });
  }

  async function run(applicationId: string, action: ComparisonAction, change: (view: ComparisonView) => ComparisonView, call: () => Promise<ComparisonView>, failure: string, outcome: Outcome): Promise<void> {
    const before = queryClient.getQueryData<ComparisonView>(key);
    mark(applicationId, action);
    if (before) queryClient.setQueryData<ComparisonView>(key, change(before));
    try {
      queryClient.setQueryData(key, await call());
      const { undo } = outcome;
      if (undo) toasts.showUndo(outcome.detail ? `${outcome.message} — ${outcome.detail}` : outcome.message, () => void takeBack(applicationId, undo, outcome.undoneMessage ?? 'Undone'));
      else toasts.show({ tone: 'success', message: outcome.message, ...(outcome.detail ? { detail: outcome.detail } : {}) });
    } catch (error) {
      if (before) queryClient.setQueryData(key, before);
      toasts.show({ tone: 'danger', message: failure, detail: errorMessage(error) });
    } finally {
      mark(applicationId, null);
    }
  }

  async function takeBack(applicationId: string, undo: () => Promise<ComparisonView>, message: string): Promise<void> {
    mark(applicationId, 'undo');
    try {
      queryClient.setQueryData(key, await undo());
      toasts.show({ tone: 'info', message });
    } catch (error) {
      toasts.show({ tone: 'danger', message: "Couldn't undo", detail: errorMessage(error) });
    } finally {
      mark(applicationId, null);
    }
  }

  const name = (column: ComparisonColumn): string => column.candidate.fullName;
  // §L33 (R8): an offer draft is made only while Offers is on. Off, N-92 tells the people involved
  // without naming who selected, so neither does the toast.
  const chosen = (candidateName: string, onMessage: string): Pick<Outcome, 'message' | 'detail'> =>
    isOffersOn ? { message: onMessage, detail: 'offer draft made' } : { message: `${candidateName} is selected`, detail: 'the people involved have been told.' };

  return {
    pendingOf: (applicationId) => pending.get(applicationId) ?? null,
    select: (column) =>
      void run(column.applicationId, 'select', (view) => patch.selected(view, column.applicationId, session.fullName, new Date()), () => comparisonApi.select(jobId, column.applicationId, column.applicationVersion), "The selection wasn't recorded", {
        ...chosen(name(column), `${name(column)} selected`),
        undo: () => comparisonApi.undoSelection(jobId, column.applicationId),
        undoneMessage: `${name(column)} is a finalist again`,
      }),
    undoSelection: (column) => void takeBack(column.applicationId, () => comparisonApi.undoSelection(jobId, column.applicationId), `Selection of ${name(column)} undone`),
    hold: (column) =>
      void run(column.applicationId, 'hold', (view) => patch.held(view, column.applicationId, new Date()), () => comparisonApi.hold(jobId, column.applicationId, column.applicationVersion), "The hold wasn't placed", {
        message: `${name(column)} held for 30 days`,
        undo: () => comparisonApi.undoHold(jobId, column.applicationId),
        undoneMessage: `${name(column)} is no longer on hold`,
      }),
    eliminate: (column, reasonCode, reasonLabel) =>
      void run(column.applicationId, 'eliminate', (view) => patch.eliminated(view, column.applicationId, reasonLabel, session.fullName, new Date()), () => comparisonApi.eliminate(jobId, column.applicationId, { reasonCode }), "Couldn't eliminate", {
        message: `${name(column)} eliminated`,
        detail: 'nothing was sent to them',
        undo: () => comparisonApi.restore(jobId, column.applicationId),
        undoneMessage: `${name(column)} is back in the comparison`,
      }),
    remove: (column) =>
      void run(column.applicationId, 'remove', (view) => patch.removed(view, column.applicationId), () => comparisonApi.remove(jobId, column.applicationId), "Couldn't take them off", {
        message: `${name(column)} taken off the comparison`,
        undo: () => comparisonApi.add(jobId, column.applicationId),
        undoneMessage: `${name(column)} is back in the comparison`,
      }),
    add: (applicationId, candidateName) =>
      void run(applicationId, 'add', (view) => patch.added(view, applicationId), () => comparisonApi.add(jobId, applicationId), "Couldn't add them", { message: `${candidateName} added to the comparison` }),
    restore: (applicationId, candidateName) =>
      void run(applicationId, 'restore', (view) => patch.restored(view, applicationId), () => comparisonApi.restore(jobId, applicationId), "Couldn't restore them", { message: `${candidateName} is back in the comparison` }),
    releaseToOffer: (row) =>
      void run(row.applicationId, 'release', (view) => patch.releasedToOffer(view, row.applicationId, session.fullName, new Date()), () => comparisonApi.release(jobId, row.applicationId, { to: 'offer', version: row.applicationVersion }), "The hold wasn't released", {
        ...chosen(row.candidateName, `${row.candidateName} released to an offer`),
        undo: () => comparisonApi.undoSelection(jobId, row.applicationId),
        undoneMessage: `${row.candidateName} is on hold again`,
      }),
    releaseToPool: (row, choice) =>
      void run(
        row.applicationId,
        'release',
        (view) => patch.releasedToPool(view, row.applicationId),
        () => comparisonApi.release(jobId, row.applicationId, { to: 'pool', version: row.applicationVersion, reasonCode: choice.reasonCode, poolIds: [...choice.poolIds], newPoolNames: [...choice.newPoolNames] }),
        "The hold wasn't released",
        { message: `${row.candidateName} released to the pool`, detail: "Their regret letter goes on this job's timing." },
      ),
    extend: (row) =>
      void run(row.applicationId, 'extend', (view) => patch.extended(view, row.applicationId, new Date()), () => comparisonApi.extend(jobId, row.applicationId), "The hold wasn't kept", { message: `${row.candidateName} kept on hold for 30 more days` }),
  };
}
